import { useEffect, useState } from "react";
import { useChains } from "src/components/FetchConfig";

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";
import { FeatureCode } from "@wallet-manager/node-types";

import {
  balanceTabRefresh,
  balanceTabRent,
} from "../../../../api/tronWalletResourceBalance";
import { CustomPagination } from "../../../../components/CustomPagination";
import { NoRowsOverlay } from "../../../../components/NoRowsOverlay";
import { useAlerting, usePermission, useTranslation } from "../../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../../hooks/useColumnHide";
import { dataGridDefaults } from "../../../../utils/constant";
import { useGenGridCol } from "../../../../utils/HelperComp";
import { useZusDialogStore } from "../../../../zustand/store";
import RefreshButton from "../components/RefreshButton";
import RentButton from "../components/RentButton";
import { getEstimateBandwidthAmountByTxAmount } from "../helpers/getEstimateBandwidthAmountByTxAmount";
import { getEstimateEnergyAmountByTxAmount } from "../helpers/getEstimateEnergyAmountByTxAmount";
import RentDialog from "../RentDialog";
import { RentDialogFields } from "../types";
import { useZusParams } from "./";
import BalanceTabRentDialogContent from "./components/BalanceTabRentDialogContent";

interface IProps {
  listMapping: (which: "key" | "name", array: any[], config?: any) => any[][];
  listRes: any;
}

const initRentDialogFields: RentDialogFields = {
  // amountEnergy: "",
  // amountBandwidth: "",
  numberOfTransactionEnergy: "",
  numberOfTransactionBandwidth: "",
  duration: "",
  receivingAddress: "",
};

function TronBalanceRecordList(props: IProps) {
  const [isRentDialogOpen, setIsRentDialogOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>({});
  const [rentDialogFields, setRentDialogFields] =
    useState<RentDialogFields>(initRentDialogFields);
  const [refreshedRow, setRefreshedRow] = useState<string[]>([]);

  const { t, te } = useTranslation("tronWalletResourceBalance");
  const { listMapping, listRes } = props;

  const { hasPermission } = usePermission();
  const zusParams = useZusParams();
  const zusDialog = useZusDialogStore();

  const { rows = [], count = 0 } = listRes?.data || { rows: [], count: 0 };

  const content: GridRowsProp = listMapping("key", rows, {
    t,
    te,
  });
  const { selectChainByPieces } = useChains();

  const { alerting } = useAlerting();
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: rentDialogFields.chainName || "",
  });

  const validateForm = () => {
    if (!rentDialogFields.numberOfTransactionEnergy) {
      alerting("error", t("phEnergyAmount"));
      return false;
    }

    if (!rentDialogFields.numberOfTransactionBandwidth) {
      alerting("error", t("phBandwidthAmount"));
      return false;
    }

    if (!rentDialogFields.duration) {
      alerting("error", t("psDuration"));
      return false;
    }

    return true;
  };

  const onRentDialogClose = () => {
    setIsRentDialogOpen(false);
    setRentDialogFields(initRentDialogFields);
  };

  const onRentConfirm = async () => {
    const payload = {
      chainType: chain_type ? Number(chain_type) : null,
      chainId: chain_id ? Number(chain_id) : null,
      walletAddress: selectedRowData?.wallet_address,
      requiredEnergy: String(rentDialogFields.numberOfTransactionEnergy),
      requiredBandwidth: String(rentDialogFields.numberOfTransactionBandwidth),
      rentPeriodSecond: String(rentDialogFields.duration),

      // orderId: getTempOrderId(),
      // merchantId: selectedRowData?.rawData?.wallet?.merchant_id || "",
    };

    if (!validateForm()) return;

    zusDialog.openExtra("loadingDialog");
    const res = await balanceTabRent(payload);
    zusDialog.closeExtra();

    if (!res) return;

    // alert and close
    alerting("success", t("submitSuccess"));
    onRentDialogClose();
  };

  const onRefresh = async (data: any) => {
    const rawData = data?.rawData || {};
    const chainType = rawData?.chain_type;
    const chainId = rawData?.chain_id;
    const walletAddress = rawData?.wallet_address;

    zusDialog.openExtra("loadingDialog");
    const res = await balanceTabRefresh({
      chainType: chainType ? Number(chainType) : null,
      chainId: chainId ? Number(chainId) : null,
      walletAddress: walletAddress,
    });
    zusDialog.closeExtra();

    if (!res) return;

    setRefreshedRow((prev) => [...prev, rawData?.id]);

    alerting("success", t("submitSuccess"));
    setSelectedRowData({}); //clear selected row after success refresh
  };

  const columns: GridColDef[] = [
    useGenGridCol("operation", t("operation"), {
      renderCell: (params: GridRenderCellParams) => {
        const isRowWalletStatusDisabled =
          Number(params.row?.rawData?.wallet?.status) === -1;

        const isRowHotWallet =
          Number(params.row?.rawData?.wallet?.wallet_type) === 2;

        const haveRefreshPermission = hasPermission(
          FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
            .TabBalance.Refresh
        );

        const haveRentPermission = hasPermission(
          FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
            .TabBalance.Rent
        );

        const isRefreshButtonDisabled =
          isRowWalletStatusDisabled ||
          refreshedRow.includes(params.row?.rawData?.id);

        const onRefreshClick = () => {
          const data = params.row;
          setSelectedRowData(data);

          onRefresh(data);
        };

        const onRentClick = () => {
          const data = params.row;
          setSelectedRowData(data);
          setIsRentDialogOpen(true);
        };

        return (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            {haveRefreshPermission && (
              <div style={{ width: "100%", textAlign: "center" }}>
                <RefreshButton
                  isDisabled={isRefreshButtonDisabled}
                  onClick={onRefreshClick}
                />
              </div>
            )}

            {haveRentPermission && (
              <div style={{ width: "100%", textAlign: "center" }}>
                <RentButton
                  isDisabled={!isRowHotWallet || isRowWalletStatusDisabled}
                  onClick={onRentClick}
                />
              </div>
            )}
          </div>
        );
      },
      hide:
        !hasPermission(
          FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
            .TabBalance.Refresh
        ) &&
        !hasPermission(
          FeatureCode.FeaturesWalletManagement.TronRentResourceBalance
            .TabBalance.Rent
        ),
      align: "center",
      minWidth: 200,
    }),
    useGenGridCol("walletDisplayName", t("walletDisplayName")),
    useGenGridCol("walletName", t("walletName")),
    useGenGridCol("walletType", t("walletType")),
    useGenGridCol("chainName", t("chainName")),

    useGenGridCol("wallet_address", t("wallet_address")),
    useGenGridCol("walletStatus", t("walletStatus")),

    useGenGridCol("energy", t("energy")),
    useGenGridCol("free_bandwidth", t("free_bandwidth")),
    useGenGridCol("delegate_bandwidth", t("delegate_bandwidth")),
    useGenGridCol("last_block_number", t("last_block_number")),
    useGenGridCol("last_block_time", t("last_block_time")),
    useGenGridCol("created_date", t("created_date")),
    useGenGridCol("last_modified_date", t("last_modified_time")),
  ].filter((col) => {
    // if (col.field === "operation" && !hasPermission(Pkey.Edit)) {
    //   return false;
    // }
    return true;
  }) as GridColDef[];

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig()
  );

  return (
    <>
      <RentDialog
        isOpen={isRentDialogOpen}
        onClose={onRentDialogClose}
        title={t("rent_resource")}
        content={
          <BalanceTabRentDialogContent
            fields={rentDialogFields}
            setFields={setRentDialogFields}
            rowData={selectedRowData}
          />
        }
        onConfirm={onRentConfirm}
      />
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        columns={columns}
        rowCount={count || 0}
        onPageChange={zusParams.setPage}
        page={zusParams.body.page}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}

export default TronBalanceRecordList;
